import { Link } from "react-router";
import { getEntriesByTopic } from "../../data/vocab/library";
import { TOPIC_DISPLAY } from "../../data/vocab/topic-display";
import type { VocabEntry } from "../../data/vocab/types";
import { WordAudioButton } from "../woorden/WordAudioButton";
import { useT } from "../../lib/use-t";

const TOPIC = "werk-beroepen" as const;

// de/het words read cleaner as a word + article pair.
const ENTRIES = getEntriesByTopic(TOPIC).filter((e) => e.gender);

/** Strip the leading "de "/"het " so the bold word and the article chip
 *  don't both show it ("het werk" → "werk" + "het"). */
function bareWord(entry: VocabEntry): string {
  return entry.dutch.replace(/^(de|het)\s+/i, "");
}

// Why: deterministic day-of-year rotation — the trio changes daily but is
// stable within a day and identical for both shared users. NOT personalization.
function wordsOfTheDay(count: number): VocabEntry[] {
  if (ENTRIES.length === 0) return [];
  const now = new Date();
  const doy = Math.floor(
    (Date.now() - Date.UTC(now.getUTCFullYear(), 0, 0)) / 86_400_000,
  );
  const start = (doy * 3) % ENTRIES.length;
  return Array.from({ length: Math.min(count, ENTRIES.length) }, (_, i) => {
    const e = ENTRIES[(start + i) % ENTRIES.length];
    return e;
  }).filter((e): e is VocabEntry => Boolean(e));
}

/**
 * Words-of-the-day mini-card. Three rows on desktop, two on mobile (the 3rd
 * row + the English column hide under lg). Source is the static vocab library
 * (fixed topic werk-beroepen, v1); the play button is the real WordAudioButton
 * wired to GET /woorden/audio.
 */
export function WoordenVandaagCard() {
  const t = useT();
  const words = wordsOfTheDay(3);
  const topicTitle = TOPIC_DISPLAY[TOPIC].title;

  return (
    <section className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-5 shadow-[var(--shadow)] sm:p-[22px]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-[17px] font-semibold tracking-tight">{t("vandaag.woorden.heading")}</h3>
        <Link
          to={`/woorden/${TOPIC}`}
          className="rounded-[var(--radius-sm)] px-3 py-1.5 text-sm font-semibold text-[var(--accent)] transition-colors hover:bg-[var(--accent-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
        >
          {topicTitle}
        </Link>
      </div>
      <ul className="flex flex-col gap-0.5">
        {words.map((w, i) => (
          <li
            key={w.id}
            className={`flex items-center gap-3 rounded-[var(--radius-sm)] px-1.5 py-2 ${
              i >= 2 ? "hidden lg:flex" : "flex"
            }`}
          >
            <WordAudioButton text={w.dutch} label="" />
            <span className="text-[14.5px] font-semibold text-[var(--ink)]">{bareWord(w)}</span>
            {w.gender ? (
              <span className="text-[13px] text-[var(--ink-faint)]">{w.gender}</span>
            ) : null}
            <span className="ml-auto hidden text-[13px] text-[var(--ink-muted)] lg:inline">
              {w.english}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
